/**
 * DocumentScanner Component for Bill Processing Agent
 * Cross-platform document scanner using device camera
 * Works on both mobile and desktop browsers
 *
 * Features:
 * - Live camera preview
 * - Multi-page capture support
 * - Auto-converts captured images to PDF
 * - Document guidance overlay
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCamera,
  faTimes,
  faPlus,
  faSpinner,
  faImage,
  faFilePdf,
  faCameraRetro,
  faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import { jsPDF } from 'jspdf';

const DocumentScanner = ({ onCapture, onClose }) => {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // Loading state for camera initialization
  const [capturedImages, setCapturedImages] = useState([]);
  const [error, setError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [facingMode, setFacingMode] = useState('environment'); // 'environment' = back camera
  const [isMobile, setIsMobile] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);

  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent.toLowerCase());
      setIsMobile(isMobileDevice);
    };
    checkMobile();
  }, []);

  // Start camera stream
  const startCamera = useCallback(async () => {
    try {
      setError(null);
      setIsLoading(true);

      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Camera access is not supported in this browser. Please use a modern browser like Chrome, Firefox, or Safari.');
      }

      // Request camera permission
      const constraints = {
        video: {
          facingMode: facingMode,
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;

      // Wait a brief moment to ensure video element is mounted
      await new Promise(resolve => setTimeout(resolve, 100));

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        setIsStreaming(true);
      } else {
        throw new Error('Video element not ready. Please try again.');
      }
    } catch (err) {
      console.error('Camera error:', err);

      let errorMessage = 'Could not access camera. ';
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        errorMessage += 'Please allow camera access in your browser settings.';
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        errorMessage += 'No camera found on this device.';
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        errorMessage += 'Camera is already in use by another application.';
      } else {
        errorMessage += err.message || 'Unknown error occurred.';
      }

      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [facingMode]);

  // Stop camera stream
  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setIsStreaming(false);
  }, []);

  // Capture image from video stream
  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Get image data as base64
    const imageData = canvas.toDataURL('image/jpeg', 0.92);

    // Add to captured images
    setCapturedImages(prev => [...prev, {
      id: Date.now(),
      data: imageData,
      width: canvas.width,
      height: canvas.height
    }]);

    // Visual feedback - flash effect
    const flashDiv = document.createElement('div');
    flashDiv.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: white;
      opacity: 0.8;
      z-index: 99999;
      pointer-events: none;
    `;
    document.body.appendChild(flashDiv);
    setTimeout(() => flashDiv.remove(), 100);

  }, []);

  // Switch between front and back camera
  const switchCamera = useCallback(async () => {
    stopCamera();
    const newMode = facingMode === 'environment' ? 'user' : 'environment';
    setFacingMode(newMode);
    // Restart camera with new facing mode after a brief delay
    setTimeout(async () => {
      try {
        const constraints = {
          video: {
            facingMode: newMode,
            width: { ideal: 1920 },
            height: { ideal: 1080 }
          },
          audio: false
        };
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
          setIsStreaming(true);
        }
      } catch (err) {
        console.error('Camera switch error:', err);
        setError('Failed to switch camera. Please try again.');
      }
    }, 100);
  }, [stopCamera, facingMode]);

  // Remove captured image
  const removeImage = useCallback((id) => {
    setCapturedImages(prev => {
      const newImages = prev.filter(img => img.id !== id);
      // If all images removed while in preview mode, go back to camera
      if (newImages.length === 0) {
        setShowPreview(false);
        setCurrentPreviewIndex(0);
      }
      return newImages;
    });
  }, []);

  // Convert captured images to PDF
  const convertToPDF = useCallback(async () => {
    if (capturedImages.length === 0) return null;

    setIsProcessing(true);
    try {
      const firstImg = capturedImages[0];

      const pdf = new jsPDF({
        orientation: firstImg.width > firstImg.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      for (let i = 0; i < capturedImages.length; i++) {
        const img = capturedImages[i];

        if (i > 0) {
          pdf.addPage(img.width > img.height ? 'landscape' : 'portrait');
        }

        // Calculate dimensions to fit page while maintaining aspect ratio
        const pageWidth = pdf.internal.pageSize.getWidth();
        const pageHeight = pdf.internal.pageSize.getHeight();

        const imgRatio = img.width / img.height;
        const pageRatio = pageWidth / pageHeight;

        let finalWidth, finalHeight, x, y;

        if (imgRatio > pageRatio) {
          finalWidth = pageWidth - 10;
          finalHeight = finalWidth / imgRatio;
          x = 5;
          y = (pageHeight - finalHeight) / 2;
        } else {
          finalHeight = pageHeight - 10;
          finalWidth = finalHeight * imgRatio;
          x = (pageWidth - finalWidth) / 2;
          y = 5;
        }

        pdf.addImage(img.data, 'JPEG', x, y, finalWidth, finalHeight);
      }

      // Convert to blob
      const pdfBlob = pdf.output('blob');
      const pdfFile = new File([pdfBlob], `scanned_bill_${Date.now()}.pdf`, {
        type: 'application/pdf'
      });

      return pdfFile;
    } catch (err) {
      console.error('PDF conversion error:', err);
      setError('Failed to create PDF: ' + err.message);
      return null;
    } finally {
      setIsProcessing(false);
    }
  }, [capturedImages]);

  // Handle done - convert to PDF and return
  const handleDone = useCallback(async () => {
    stopCamera();
    const pdfFile = await convertToPDF();
    if (pdfFile && onCapture) {
      onCapture(pdfFile);
    }
    if (onClose) {
      onClose();
    }
  }, [stopCamera, convertToPDF, onCapture, onClose]);

  // Handle close
  const handleClose = useCallback(() => {
    stopCamera();
    if (onClose) {
      onClose();
    }
  }, [stopCamera, onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopCamera();
    };
  }, [stopCamera]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-gray-900 text-white">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <FontAwesomeIcon icon={faCameraRetro} />
          Scan Bill Document
        </h2>
        <button
          onClick={handleClose}
          className="p-2 hover:bg-gray-700 rounded-full transition-colors"
        >
          <FontAwesomeIcon icon={faTimes} className="text-xl" />
        </button>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Error Display */}
        {error && (
          <div className="m-4 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-300">
            <p>{error}</p>
            <button
              onClick={() => setError(null)}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Camera View */}
        {!showPreview && (
          <div className="flex-1 relative flex items-center justify-center bg-black">
            {/* Initial State - Show Start Camera button */}
            {!isStreaming && !isLoading && (
              <div className="text-center text-white p-8">
                <FontAwesomeIcon icon={faCamera} className="text-6xl mb-4 text-gray-400" />
                <h3 className="text-xl mb-4">Ready to Scan</h3>
                <p className="text-gray-400 mb-6">
                  Position your bill/invoice in good lighting and tap the button below to start the camera.
                </p>
                <button
                  onClick={startCamera}
                  className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-semibold rounded-xl
                           hover:from-indigo-600 hover:to-purple-700 transition-all shadow-lg"
                >
                  <FontAwesomeIcon icon={faCamera} className="mr-2" />
                  Start Camera
                </button>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="text-center text-white p-8">
                <FontAwesomeIcon icon={faSpinner} className="text-6xl mb-4 text-indigo-400 animate-spin" />
                <h3 className="text-xl mb-2">Starting Camera...</h3>
                <p className="text-gray-400">Please allow camera access if prompted</p>
              </div>
            )}

            {/* Video Element - Always rendered but hidden when not streaming */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className={`max-h-full max-w-full object-contain ${isStreaming ? 'block' : 'hidden'}`}
              style={{ transform: facingMode === 'user' ? 'scaleX(-1)' : 'none' }}
            />

            {/* Document Overlay Guide - Show when streaming */}
            {isStreaming && (
              <>
                <div className="absolute inset-8 border-2 border-dashed border-white/50 rounded-lg pointer-events-none">
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-white rounded-tl-lg" />
                  <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-white rounded-tr-lg" />
                  <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-white rounded-bl-lg" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-white rounded-br-lg" />
                  <p className="absolute -top-6 left-1/2 -translate-x-1/2 text-white/70 text-sm whitespace-nowrap">
                    Align bill within frame
                  </p>
                </div>

                {/* Camera Switch Button (mobile) */}
                {isMobile && (
                  <button
                    onClick={switchCamera}
                    className="absolute top-4 right-4 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
                    title="Switch Camera"
                  >
                    <FontAwesomeIcon icon={faSyncAlt} />
                  </button>
                )}
              </>
            )}

            {/* Hidden Canvas for capture */}
            <canvas ref={canvasRef} className="hidden" />
          </div>
        )}

        {/* Preview Mode - Show captured images */}
        {showPreview && capturedImages.length > 0 && (
          <div className="flex-1 flex flex-col bg-gray-900 p-4 overflow-hidden">
            <div className="flex-1 flex items-center justify-center mb-4 overflow-hidden">
              <img
                src={capturedImages[currentPreviewIndex]?.data}
                alt={`Page ${currentPreviewIndex + 1}`}
                className="max-h-full max-w-full object-contain rounded-lg shadow-xl"
              />
            </div>

            {/* Thumbnail Strip */}
            <div className="flex gap-2 overflow-x-auto pb-2 justify-center">
              {capturedImages.map((img, idx) => (
                <div
                  key={img.id}
                  onClick={() => setCurrentPreviewIndex(idx)}
                  className={`relative flex-shrink-0 cursor-pointer rounded-lg overflow-hidden transition-all ${
                    idx === currentPreviewIndex ? 'ring-2 ring-blue-500 scale-105' : 'opacity-60 hover:opacity-100'
                  }`}
                >
                  <img
                    src={img.data}
                    alt={`Thumbnail ${idx + 1}`}
                    className="w-16 h-20 object-cover"
                  />
                  <span className="absolute bottom-1 right-1 bg-black/70 text-white text-xs px-1 rounded">
                    {idx + 1}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Adjust preview index before removing
                      if (currentPreviewIndex >= capturedImages.length - 1 && currentPreviewIndex > 0) {
                        setCurrentPreviewIndex(currentPreviewIndex - 1);
                      }
                      removeImage(img.id);
                    }}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full text-xs
                             flex items-center justify-center hover:bg-red-600"
                  >
                    <FontAwesomeIcon icon={faTimes} className="text-xs" />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Captured Images Counter (when camera is streaming) */}
        {isStreaming && capturedImages.length > 0 && (
          <button
            onClick={() => setShowPreview(true)}
            className="absolute bottom-24 left-4 px-4 py-2 bg-green-600 text-white rounded-full
                     flex items-center gap-2 shadow-lg hover:bg-green-700 transition-colors"
          >
            <FontAwesomeIcon icon={faImage} />
            {capturedImages.length} page{capturedImages.length > 1 ? 's' : ''} captured
          </button>
        )}
      </div>

      {/* Footer Controls */}
      <div className="p-4 bg-gray-900 border-t border-gray-700">
        <div className="flex items-center justify-center gap-4">
          {/* Back to Camera Button (from preview) */}
          {showPreview && (
            <button
              onClick={() => {
                setShowPreview(false);
                startCamera();
              }}
              className="px-6 py-3 bg-gray-600 text-white rounded-xl flex items-center gap-2
                       hover:bg-gray-700 transition-colors"
            >
              <FontAwesomeIcon icon={faPlus} />
              Add More Pages
            </button>
          )}

          {/* Capture Button */}
          {isStreaming && !showPreview && (
            <button
              onClick={captureImage}
              className="px-8 py-4 bg-gradient-to-r from-indigo-500 to-purple-600 text-white font-bold text-lg
                       rounded-xl flex items-center gap-3 shadow-lg hover:from-indigo-600 hover:to-purple-700
                       hover:scale-105 active:scale-95 transition-all"
            >
              <FontAwesomeIcon icon={faCamera} className="text-2xl" />
              Capture Page
            </button>
          )}

          {/* Submit Button */}
          {capturedImages.length > 0 && (
            <button
              onClick={handleDone}
              disabled={isProcessing}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold text-lg
                       rounded-xl flex items-center gap-3 shadow-lg
                       hover:from-green-600 hover:to-emerald-700 transition-all
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} className="animate-spin text-2xl" />
                  Creating PDF...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faFilePdf} className="text-2xl" />
                  Submit Scan ({capturedImages.length} page{capturedImages.length > 1 ? 's' : ''})
                </>
              )}
            </button>
          )}
        </div>

        {/* Instructions */}
        {isStreaming && !showPreview && (
          <p className="text-center text-gray-300 text-base mt-4">
            {capturedImages.length === 0
              ? '👆 Tap "Capture Page" to scan your bill'
              : `✅ ${capturedImages.length} page${capturedImages.length > 1 ? 's' : ''} captured. Tap "Submit Scan" when done, or capture more pages.`
            }
          </p>
        )}
      </div>
    </div>
  );
};

export default DocumentScanner;
